import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { api } from '../services/api';
import { useReadOnly } from '../hooks/useReadOnly';

/**
 * The new route plan — look first, then apply.
 *
 * The owner, Oct 1 2026: Monday–Thursday laid out again in a sheet, every day
 * in a Week A and a Week B, starting Mon Oct 5. No bridge or pull-forward mows
 * (each client's first mow on the plan is their next mow), every-4-week yards
 * stay every 4 weeks, and every client whose day changes gets one text that he
 * has read and left ticked. Customer satisfaction first.
 *
 * "Look first" runs the whole thing on the server and throws it away, so what
 * is on this screen is exactly what Apply will do. Apply can be pressed again:
 * a yard already moved is left alone and a text already sent is never sent
 * twice — so a text he held back can be ticked and sent later.
 *
 * A text is only ever sent for what the screen showed him. A draft he has not
 * typed in takes the server's wording on every look (the date in it may have
 * moved); one he typed in keeps his words and is unticked when its date moves.
 * A text held back after an Apply, and a correction to a text that already
 * went, come back UNTICKED. Whatever he picked by hand ("It is …", "Yes, same
 * yard", another first date) is listed with an Undo, and goes when the sheet
 * changes or the panel closes.
 */

// What he sees for one text: `edited` — the words are his; next_mow / now — the facts they were shown beside.
type Draft = { body: string; send: boolean; edited: boolean; next_mow: string | null; now: string };
const textKey = (t: any) => `${t.schedule_id}:${t.seq || 1}`;

const day = (iso?: string | null) => (iso
  ? new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
  : '—');

export default function RoutePlanPanel() {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [csv, setCsv] = useState('');
  const [look, setLook] = useState<any>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [picks, setPicks] = useState<Record<string, number>>({});
  const [confirm, setConfirm] = useState<string[]>([]);
  const [starts, setStarts] = useState<Record<string, string>>({});
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});

  const { data: last } = useQuery('route-plan', () => api.get('/routes/plan').then(r => r.data), { staleTime: 60_000 });

  // What he typed or unticked survives another look; a text that has gone is never ticked again. A tick only
  // survives while the day and the date it was made against are still the ones on the screen.
  const takeTexts = (d: any) => setDrafts(prev => {
    const next: Record<string, Draft> = {};
    for (const t of d?.texts || []) {
      const mine = prev[textKey(t)];
      const live = !t.sent_at && !t.sending_at;
      const hisWords = !!mine && mine.edited && live;
      const sameFacts = !!mine && mine.next_mow === (t.next_mow || null) && mine.now === t.now;
      next[textKey(t)] = {
        body: hisWords ? mine.body : (t.body || ''),
        edited: hisWords || !!t.edited,
        send: live && !!t.can_text && !t.stale && !t.held && (mine ? mine.send && sameFacts : !t.kept && !t.correction),
        next_mow: t.next_mow || null, now: t.now,
      };
    }
    return next;
  });

  const run = useMutation(
    (v: { apply: boolean; picks?: Record<string, number>; confirm?: string[]; starts?: Record<string, string> }) => api.post(v.apply ? '/routes/plan/apply' : '/routes/plan/preview', {
      csv, picks: v.picks || picks, confirm: v.confirm || confirm, starts: v.starts || starts,
      texts: v.apply ? Object.entries(drafts).map(([k, t]) => ({
        schedule_id: Number(k.split(':')[0]), seq: Number(k.split(':')[1]), body: t.body, edited: t.edited, send: t.send, next_mow: t.next_mow,
      })) : [],
    }).then(r => r.data),
    {
      onSuccess: (d: any) => {
        setProblems([]); setLook(d); takeTexts(d);
        if (!d.dry_run) {
          toast.success(d.changes ? 'The plan is on the calendar' : 'Nothing to change — the calendar already matches the plan', { autoClose: 9000 });
          qc.invalidateQueries('routes'); qc.invalidateQueries('route-odd-days'); qc.invalidateQueries('route-plan');
        }
      },
      onError: (e: any) => {
        setProblems(e?.response?.data?.problems || []);
        toast.error(e?.response?.data?.message || 'Could not read that plan');
      },
    });

  const pick = (key: string, scheduleId: number) => { const p = { ...picks, [key]: scheduleId }; setPicks(p); run.mutate({ apply: false, picks: p }); };
  const sameYard = (key: string) => { const c = [...confirm, key]; setConfirm(c); run.mutate({ apply: false, confirm: c }); };
  // A mis-tap is taken back: the yard goes back under "Needs you", untouched.
  const undo = (key: string) => {
    const p = { ...picks }; delete p[key];
    const c = confirm.filter(k => k !== key);
    setPicks(p); setConfirm(c); run.mutate({ apply: false, picks: p, confirm: c });
  };
  const startOn = (key: string, date: string | null) => {
    const s = { ...starts }; if (date) s[key] = date; else delete s[key];
    setStarts(s); run.mutate({ apply: false, starts: s });
  };
  // Another sheet (or the panel closed) is another plan: nothing he picked or typed for the last one carries over.
  const fresh = () => { setLook(null); setProblems([]); setPicks({}); setConfirm([]); setStarts({}); setDrafts({}); };
  const readFile = (f?: File | null) => { if (!f) return; f.text().then(t => { setCsv(t); fresh(); }); };

  if (!open) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={() => setOpen(true)} className="text-xs font-semibold text-brand-700 underline underline-offset-2">
          New route plan
        </button>
        {last?.plan_start && (
          <span className="text-[11px] text-gray-500">
            The plan from {day(last.plan_start)} is applied · {last.sent} text{last.sent === 1 ? '' : 's'} sent{last.not_sent ? `, ${last.not_sent} not sent` : ''}
          </span>
        )}
      </div>
    );
  }

  const texts: any[] = look?.texts || [];
  const toSend = texts.filter(t => drafts[textKey(t)]?.send && !t.sent_at).length;
  const checks = look?.checks || {};
  const moving: any[] = (look?.yards || []).filter((y: any) => y.status === 'moves');
  const byHand: any[] = (look?.yards || []).filter((y: any) => picks[y.key] !== undefined || confirm.includes(y.key));
  const firstMows: any[] = (look?.yards || []).filter((y: any) => y.start_choice);
  const handOf = new Map<number, string>(byHand.map((y: any) => [y.schedule_id, y.matched_by]));

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-gray-900">New route plan</p>
        <button type="button" onClick={() => { setOpen(false); fresh(); }} className="text-xs text-gray-500">Close</button>
      </div>
      <p className="text-[11px] text-gray-500">
        Put the plan sheet in — Day, Week, First_date, Stop, Client, Address, Frequency. It matches each row on the address, then the
        name, and shows you everything before it changes anything. No bridge or pull-forward mows: each client&apos;s first mow on the
        plan is their next mow. How often a yard is mowed never changes here.
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <input type="file" accept=".csv,text/csv" onChange={e => readFile(e.target.files?.[0])} className="text-xs" />
        <span className="text-[11px] text-gray-400">or paste it</span>
      </div>
      <textarea value={csv} onChange={e => { setCsv(e.target.value); fresh(); }} rows={4}
        placeholder={'Day,Week,First_date,Stop,Arrive,Client,Address,Mow_min,Frequency,Drive_to_next_min\nMonday,A,Mon Oct 12,1,8:00am,Mimi,"205 Seminole Canyon Dr, Georgetown",24,every week,1'}
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-xs font-mono" />

      {problems.length > 0 && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-2.5 text-xs text-red-800 space-y-0.5">
          <p className="font-semibold">Fix the sheet first — nothing was looked at:</p>
          {problems.map((p, i) => <p key={i}>{p}</p>)}
        </div>
      )}

      {look && (
        <div className="space-y-3">
          <div className={`rounded-lg border p-2.5 text-xs space-y-0.5 ${look.dry_run ? 'bg-gray-50 border-gray-200 text-gray-800' : 'bg-emerald-50 border-emerald-200 text-emerald-900'}`}>
            <p className="font-semibold">{look.dry_run ? 'This is what it would do:' : 'Applied:'}</p>
            {(look.summary || []).map((s: string, i: number) => <p key={i}>{s}</p>)}
          </div>

          <AtAGlance look={look} />

          <div className="text-xs text-gray-800">
            <p className="font-semibold mb-0.5">Routes</p>
            {(look.routes || []).map((r: any) => (
              <p key={`${r.day}${r.week}`}>
                {r.day} Week {r.week} · {r.stops} stops → <b>{r.route}</b>
                <span className="text-gray-500"> — {r.renamed_from ? `"${r.renamed_from}", ${r.why}` : r.why}</span>
              </p>
            ))}
          </div>

          {(look.need_you || []).length > 0 && (
            <div className="rounded-lg bg-amber-50 border border-amber-300 p-2.5 text-xs text-amber-900 space-y-1.5">
              <p className="font-semibold">Needs you — these yards are not touched until you say:</p>
              {look.need_you.map((n: any) => (
                <div key={n.key}>
                  <p><b>{n.client}</b> · {n.address} · {n.day} — {n.why}</p>
                  {n.kind === 'confirm_name' && (
                    <button type="button" disabled={readOnly || run.isLoading} onClick={() => sameYard(n.key)}
                      className="mt-0.5 px-2 py-1 rounded-lg bg-amber-700 text-white font-semibold disabled:opacity-50">Yes, same yard</button>
                  )}
                  {n.kind !== 'confirm_name' && (n.candidates || []).map((c: any) => (
                    <button key={c.schedule_id} type="button" disabled={readOnly || run.isLoading} onClick={() => pick(n.key, c.schedule_id)}
                      className="mt-0.5 mr-1 px-2 py-1 rounded-lg border border-amber-400 bg-white disabled:opacity-50">
                      It is {c.customer} — {c.address} ({c.mows})
                    </button>
                  ))}
                  {(picks[n.key] !== undefined || confirm.includes(n.key)) && (
                    <button type="button" disabled={run.isLoading} onClick={() => undo(n.key)} className="mt-0.5 underline underline-offset-2 disabled:opacity-50">
                      Undo what you picked for this one
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {byHand.length > 0 && (
            <div className="rounded-lg bg-amber-50 border border-amber-300 p-2.5 text-xs text-amber-900 space-y-1">
              <p className="font-semibold">Matched by you — these WILL be moved. Check each one:</p>
              {byHand.map((y: any) => (
                <p key={y.key}>
                  The plan&apos;s <b>{y.client}</b> · {y.address} is <b>{y.customer}</b> in the app ({y.matched_by}).{' '}
                  <button type="button" disabled={run.isLoading} onClick={() => undo(y.key)} className="underline underline-offset-2 font-semibold disabled:opacity-50">Undo</button>
                </p>
              ))}
            </div>
          )}

          {firstMows.length > 0 && (
            <div className="rounded-lg bg-amber-50 border border-amber-300 p-2.5 text-xs text-amber-900 space-y-1.5">
              <p className="font-semibold">First mow on the plan — check these</p>
              <p>An every-4-week yard takes the fortnight its own 4 weeks fall on, and a first visit already further out is not pulled forward. Pick the other date if you want it.</p>
              {firstMows.map((y: any) => {
                const c = y.start_choice;
                return (
                  <div key={y.key}>
                    <p>
                      <b>{y.customer}</b> · {y.now} · last mow {day(y.last_mow)} → first on the plan <b>{day(c.start)}</b>
                      {y.gap_days !== null && y.gap_days !== undefined ? ` (${y.gap_days} days)` : ''}
                      {c.picked ? ' — you picked it' : c.kept_cycle ? ' — the date they already had' : c.start !== c.slot_first ? ` — the slot's first date is ${day(c.slot_first)}` : ''}
                    </p>
                    {(c.options || []).map((o: any) => (
                      <button key={o.date} type="button" disabled={readOnly || run.isLoading} onClick={() => startOn(y.key, o.date)}
                        className="mt-0.5 mr-1 px-2 py-1 rounded-lg border border-amber-400 bg-white disabled:opacity-50">
                        Start {day(o.date)} instead{o.gap_days !== null && o.gap_days !== undefined ? ` (${o.gap_days} days after the last mow)` : ''}
                      </button>
                    ))}
                    {c.picked && (
                      <button type="button" disabled={run.isLoading} onClick={() => startOn(y.key, null)} className="mt-0.5 underline underline-offset-2 disabled:opacity-50">Undo</button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {texts.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-900">
                Texts — read each one. Only the ticked ones are sent, once, when you apply.
              </p>
              {texts.map((t: any) => {
                const k = textKey(t);
                const d: Draft = drafts[k] || { body: t.body || '', send: false, edited: !!t.edited, next_mow: t.next_mow || null, now: t.now };
                const done = !!t.sent_at;
                return (
                  <div key={k} className={`rounded-lg border p-2.5 text-xs space-y-1 ${done ? 'bg-emerald-50 border-emerald-200' : t.correction ? 'bg-amber-50 border-amber-300' : 'bg-white border-gray-200'}`}>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={d.send} disabled={done || !t.can_text || !!t.sending_at}
                        onChange={e => setDrafts(p => ({ ...p, [k]: { ...d, send: e.target.checked } }))} />
                      <span>
                        {t.correction && <b>Correction · </b>}<b>{t.customer}</b> · {t.was} → {t.now} · next mow {day(t.next_mow)}{t.phone_ends ? ` · …${t.phone_ends}` : ''}
                        {handOf.has(t.schedule_id) ? ` · matched by you (${handOf.get(t.schedule_id)})` : ''}
                      </span>
                    </label>
                    {t.told && !done && <p className="text-amber-800 font-semibold">{t.told}. They have not been told about this change — tick this to send the correction.</p>}
                    {done ? <p className="text-emerald-800">Sent. {t.body}</p> : (
                      <textarea value={d.body} rows={3} disabled={!!t.sending_at}
                        onChange={e => setDrafts(p => ({ ...p, [k]: { ...d, body: e.target.value, edited: true } }))}
                        className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-xs" />
                    )}
                    {t.stale && !done && <p className="text-amber-700 font-semibold">{t.stale}</p>}
                    {t.held && !done && <p className="text-amber-700 font-semibold">Not sent — {t.held}.</p>}
                    {!done && d.edited && t.draft && d.body !== t.draft && (
                      <button type="button" onClick={() => setDrafts(p => ({ ...p, [k]: { ...d, body: t.draft, edited: false } }))}
                        className="underline underline-offset-2 text-gray-600">Use the wording for {day(t.next_mow)} instead of mine</button>
                    )}
                    {t.kept && !done && !t.stale && !t.held && !d.send && t.can_text && <p className="text-gray-500">Held back earlier — tick it to send it now.</p>}
                    {!t.can_text && !done && <p className="text-amber-700">Cannot be texted — {t.why_not}. Call them.</p>}
                    {t.sending_at && !done && <p className="text-amber-700">This one was being sent when something stopped — check your phone before sending it again.</p>}
                    {t.error && !done && <p className="text-red-700">{t.error}</p>}
                  </div>
                );
              })}
            </div>
          )}

          {[['Already told a different day or date — a correction is drafted above, unticked', checks.told_differently],
            ['Too soon after the last mow — free at month-end unless you change it', checks.too_soon],
            ['Every 4 weeks, but mowed a week or more early', checks.early],
            ['A longer wait than usual for the first mow (no bridge mows)', checks.long_gaps],
            ['A person set by hand on a visit that moved — check who works it', checks.own_crew],
            ['Lawn-plan visits with no mow at the yard that day', checks.plan_visits_alone],
            ['Did not read back right', [...(checks.two_visits_one_day || []), ...(checks.riders_off_their_mow || [])]],
            ['Two routes out on one day', checks.two_routes_one_day],
            ['Stop order', checks.order],
            ['Residential mows on a Friday (Friday B is commercial only)', look.fridays],
          ].filter(([, list]) => (list as string[] || []).length > 0).map(([title, list]) => (
            <div key={title as string} className="text-xs text-gray-800">
              <p className="font-semibold">{title as string}</p>
              {(list as string[]).map((s, i) => <p key={i}>{s}</p>)}
            </div>
          ))}

          {(look.not_on_plan || []).length > 0 && (
            <details className="text-xs text-gray-800">
              <summary className="font-semibold cursor-pointer">Mows on a Monday–Thursday that are not on the plan — left where they are ({look.not_on_plan.length})</summary>
              {look.not_on_plan.map((n: any, i: number) => <p key={i}><b>{n.customer}</b> · {n.address} — {n.visits.join(', ')}</p>)}
            </details>
          )}

          {moving.length > 0 && (
            <details className="text-xs text-gray-800">
              <summary className="font-semibold cursor-pointer">Every client changing day or week ({moving.length})</summary>
              {moving.map((y: any) => (
                <div key={y.schedule_id} className="mt-1">
                  <p><b>{y.customer}</b> · {y.was} → {y.now} on {y.route} · last mow {day(y.last_mow)}, next {day(y.next_mow)}</p>
                  {(y.visits || []).filter((v: any) => v.addons.length || v.riders.length).map((v: any) => (
                    <p key={v.job_id} className="text-gray-600">{day(v.from)} → {day(v.to)}: {[...v.addons, ...v.riders].join(', ')} goes with it</p>
                  ))}
                  {(y.flags || []).map((f: string, i: number) => <p key={i} className="text-amber-700">{f}</p>)}
                </div>
              ))}
            </details>
          )}

          {(look.first_weeks || []).length > 0 && (
            <details className="text-xs text-gray-800">
              <summary className="font-semibold cursor-pointer">The first two weeks, stop by stop</summary>
              {look.first_weeks.map((f: any) => (
                <p key={`${f.route_id}${f.date}`} className="mt-1">
                  <b>{day(f.date)} · {f.route}</b>: {f.stops.map((s: any) => `${s.name}${s.on_plan ? '' : ' (not on the plan)'}${s.skipped ? ' (skipped)' : ''}`).join(' → ')}
                </p>
              ))}
            </details>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={() => run.mutate({ apply: false })} disabled={readOnly || run.isLoading || !csv.trim()}
          className="text-xs px-2.5 py-1.5 rounded-lg bg-gray-800 text-white font-semibold disabled:opacity-50">
          {run.isLoading ? 'Working…' : 'Look first'}
        </button>
        {look && (
          <button type="button" onClick={() => run.mutate({ apply: true })} disabled={readOnly || run.isLoading}
            className="text-xs px-2.5 py-1.5 rounded-lg bg-emerald-700 text-white font-semibold disabled:opacity-50">
            {look.dry_run ? 'Apply the plan' : 'Apply again'} — send {toSend} text{toSend === 1 ? '' : 's'}
          </button>
        )}
        {look && moving.length > 0 && (
          // Owner, Oct 1 2026: his ops assistant asked for "the app's last-mow date for each" to check the gaps.
          <button type="button"
            onClick={async () => {
              const rows = [
                'Client, was, new, last mow, first mow on the plan, days between',
                ...moving.map((y: any) => [y.customer, y.was, y.now, y.last_mow || 'none', y.next_mow || '', y.gap_days ?? ''].join(', ')),
              ].join('\n');
              try { await navigator.clipboard.writeText(rows); toast.success(`Copied ${moving.length} clients`); }
              catch { toast.error('Could not copy — select the list under "Every client changing day or week" instead'); }
            }}
            className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 text-gray-700 font-semibold">
            Copy the list
          </button>
        )}
        <span className="text-[11px] text-gray-500">No automatic &quot;visit moved&quot; texts go out. Nobody is charged.</span>
      </div>
    </div>
  );
}

/**
 * The next two weeks at a glance (owner, Oct 1 2026: "give me understandable data to see what is going to change
 * next week if we move routes"). The same Look-first answer, drawn instead of listed: each day's routes with their
 * stops in order — who keeps their day, who moves (and from when), who is new to the plan — and one table of every
 * customer whose day changes, with the gap from their last mow to their first mow on the plan.
 */
function gapTone(gap: number | null | undefined, every: number) {
  if (gap == null) return { cls: 'text-gray-500', word: '' };
  if (gap > every + 3) return { cls: 'text-red-700 font-bold', word: 'long wait' };
  if (gap < Math.max(every - 2, 5)) return { cls: 'text-blue-700 font-bold', word: 'soon' };
  return { cls: 'text-gray-800', word: '' };
}

function AtAGlance({ look }: { look: any }) {
  const yards: any[] = look?.yards || [];
  const weeks: any[] = look?.first_weeks || [];
  if (!yards.length && !weeks.length) return null;
  const byName = new Map<string, any>(yards.map((y: any) => [String(y.customer || '').toLowerCase(), y]));
  const moves = yards.filter(y => y.status === 'moves').sort((a, b) => (b.gap_days ?? -1) - (a.gap_days ?? -1));
  const long = yards.filter(y => gapTone(y.gap_days, y.every).word === 'long wait').length;
  const soon = yards.filter(y => gapTone(y.gap_days, y.every).word === 'soon').length;
  const dates = Array.from(new Set(weeks.map(w => w.date))).sort();
  const first = dates[0];
  const weekOf = (d: string) => (first && (new Date(`${d}T12:00:00`).getTime() - new Date(`${first}T12:00:00`).getTime()) / 86400000 >= 7 ? 1 : 0);
  const often = (n: number) => (n === 7 ? 'weekly' : n === 14 ? 'every 2 wks' : n === 28 ? 'every 4 wks' : `every ${n} days`);

  return (
    <div className="rounded-lg border border-brand-200 bg-white p-2.5 text-xs text-gray-800 space-y-3">
      <div>
        <p className="font-bold text-sm text-gray-900">The next two weeks at a glance</p>
        <div className="mt-1 flex flex-wrap gap-2">
          <span className="rounded-full bg-gray-100 px-2 py-0.5">{yards.length - moves.length} keep their day</span>
          <span className="rounded-full bg-amber-100 text-amber-900 px-2 py-0.5 font-semibold">{moves.length} change day</span>
          {long > 0 && <span className="rounded-full bg-red-100 text-red-800 px-2 py-0.5 font-semibold">{long} long wait{long === 1 ? '' : 's'}</span>}
          {soon > 0 && <span className="rounded-full bg-blue-100 text-blue-800 px-2 py-0.5 font-semibold">{soon} mowed sooner than usual</span>}
        </div>
        <p className="mt-1 text-[11px] text-gray-600">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-amber-200 align-middle" /> changes day ·{' '}
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-gray-100 border border-gray-300 align-middle" /> same day ·{' '}
          <span className="inline-block w-2.5 h-2.5 rounded-sm border border-dashed border-gray-400 align-middle" /> on the route but not on the plan · days = since their last mow
        </p>
      </div>

      {[0, 1].map(wk => {
        const ds = dates.filter(d => weekOf(d) === wk);
        if (!ds.length) return null;
        return (
          <div key={wk}>
            <p className="font-semibold text-gray-700 mb-1">{wk === 0 ? 'First week' : 'Second week'}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {ds.map(d => (
                <div key={d} className="rounded-lg border border-gray-200 p-2">
                  <p className="font-bold text-gray-900">{new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</p>
                  {weeks.filter(w => w.date === d).map(w => (
                    <div key={`${w.route_id}${d}`} className="mt-1.5">
                      <p className="text-[11px] font-semibold text-brand-700">{w.route} · {(n => `${n} stop${n === 1 ? '' : 's'}`)(w.stops.filter((x: any) => !x.skipped).length)}</p>
                      <ol className="mt-0.5 space-y-0.5">
                        {w.stops.map((x: any, i: number) => {
                          const y = byName.get(String(x.name || '').toLowerCase());
                          const moved = y?.status === 'moves';
                          const tone = gapTone(y?.gap_days, y?.every || 14);
                          return (
                            <li key={i} className={`rounded px-1.5 py-0.5 flex items-baseline gap-1 ${x.skipped ? 'line-through text-gray-400' : moved ? 'bg-amber-100' : x.on_plan ? 'bg-gray-50' : 'border border-dashed border-gray-300'}`}>
                              <span className="text-gray-500 w-4 text-right">{i + 1}</span>
                              <span className="font-medium truncate">{x.name}</span>
                              {moved && y?.was && <span className="text-[10px] text-amber-800 whitespace-nowrap">was {y.was}</span>}
                              {y?.gap_days != null && <span className={`ml-auto text-[10px] whitespace-nowrap ${tone.cls}`}>{y.gap_days}d{tone.word ? ` · ${tone.word}` : ''}</span>}
                            </li>
                          );
                        })}
                      </ol>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {moves.length > 0 && (
        <div>
          <p className="font-semibold text-gray-700 mb-1">Everyone whose day changes ({moves.length}) — longest wait first</p>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="text-[11px] text-gray-500">
                <tr><th className="py-1 pr-2">Customer</th><th className="pr-2">Was</th><th className="pr-2">Now</th><th className="pr-2">Last mow</th><th className="pr-2">First new mow</th><th className="pr-2">Wait</th><th>How often</th></tr>
              </thead>
              <tbody>
                {moves.map((y: any) => {
                  const tone = gapTone(y.gap_days, y.every);
                  const fmt = (iso?: string | null) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '—');
                  return (
                    <tr key={y.key} className="border-t border-gray-100">
                      <td className="py-1 pr-2 font-medium">{y.customer}</td>
                      <td className="pr-2 text-gray-600">{y.was || '—'}</td>
                      <td className="pr-2 font-semibold">{y.now}</td>
                      <td className="pr-2">{fmt(y.last_mow)}</td>
                      <td className="pr-2">{fmt(y.next_mow || y.starts)}</td>
                      <td className={`pr-2 ${tone.cls}`}>{y.gap_days != null ? `${y.gap_days} days` : '—'}{tone.word ? ` (${tone.word})` : ''}</td>
                      <td className="text-gray-600">{often(y.every)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
